/**
 * Persisted display preferences for the mobile Customers screen — group by,
 * sort, and which row-detail fields show. Scoped per store in localStorage so
 * the choice survives reloads. Clone of useProductsDisplayPrefs.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  CustomersGroupBy,
  CustomersSort,
  RowProp,
} from "./customers-mobile-model";

export type RowProps = Record<RowProp, boolean>;

export type CustomersDisplayPrefs = {
  group: CustomersGroupBy;
  sort: CustomersSort;
  props: RowProps;
};

const DEFAULTS: CustomersDisplayPrefs = {
  group: "none",
  sort: "spend",
  props: {
    governorate: true,
    orders: true,
    since: true,
  },
};

const VERSION = 1;
const key = (storeSlug: string) => `seller:customers-display:v${VERSION}:${storeSlug}`;

function read(storeSlug: string): CustomersDisplayPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(key(storeSlug));
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<CustomersDisplayPrefs>;
    return {
      group: parsed.group ?? DEFAULTS.group,
      sort: parsed.sort ?? DEFAULTS.sort,
      props: { ...DEFAULTS.props, ...(parsed.props ?? {}) },
    };
  } catch {
    return DEFAULTS;
  }
}

export function useCustomersDisplayPrefs(storeSlug: string) {
  const [prefs, setPrefs] = useState<CustomersDisplayPrefs>(() => read(storeSlug));

  useEffect(() => {
    setPrefs(read(storeSlug));
  }, [storeSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key(storeSlug), JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs, storeSlug]);

  const setGroup = useCallback((group: CustomersGroupBy) => setPrefs((p) => ({ ...p, group })), []);
  const setSort = useCallback((sort: CustomersSort) => setPrefs((p) => ({ ...p, sort })), []);
  const toggleProp = useCallback(
    (prop: RowProp) => setPrefs((p) => ({ ...p, props: { ...p.props, [prop]: !p.props[prop] } })),
    [],
  );
  const resetProps = useCallback(() => setPrefs((p) => ({ ...p, props: { ...DEFAULTS.props } })), []);

  return { prefs, setGroup, setSort, toggleProp, resetProps };
}
